/**
 * Diff-review — renderer for the per-change review tasks (LEGAL-3).
 *
 * When a delta proposes a new obligation version, the current PENDING version
 * supersedes a previously confirmed one. This screen shows the old confirmed
 * version beside the new proposal with changed fields highlighted, allows
 * inline adjustment, then confirm / needsRevision / reject through the same
 * append-only review API. (Until LEGAL-3 produces such pairs, the worklist
 * is empty and says so.)
 */

import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI, LegalReviewAction } from "../../LegalAdministrationAPI";
import {
  ReviewStatusBadge,
  ProvenanceBadge,
  EffectivePendingWarning,
  obligationFieldLabel,
  regulatedObjectName,
  formatDate,
} from "./legalDisplay";
import { ApprovalBlockHint, ApprovalTrail, approveLabel, reviewDialogTexts, useFourEyes } from "./fourEyes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

interface Properties {
  vocabulary: any;
  regulatedObjects: any[];
  refreshSignal: number;
  onReviewed: () => void;
}

const DIFF_FIELDS = [
  "type",
  "modality",
  "regulatedObjectCode",
  "roleCode",
  "protectionGoalCode",
  "description",
  "inspection",
  "validFrom",
];

const enumLabel = (t: any, prefix: string, value: any): string =>
  value ? String(t(`operatorResponsibilityPanel.${prefix}.${value}`, { defaultValue: value })) : "—";

const fieldValue = (t: any, obligation: any, field: string) => {
  if (!obligation) {
    return "—";
  }

  if (field === "inspection") {
    return obligation.inspection
      ? `${enumLabel(t, "inspectionTriggerValue", obligation.inspection.trigger)} / ${enumLabel(
          t,
          "inspectorValue",
          obligation.inspection.inspector,
        )} / ${
          obligation.inspection.maxIntervalMonths != null
            ? t("operatorResponsibilityPanel.recurrenceMonths", {
                months: obligation.inspection.maxIntervalMonths,
              })
            : "—"
        }`
      : "—";
  }

  if (field === "type") {
    return enumLabel(t, "obligationType", obligation.type);
  }

  if (field === "modality") {
    return enumLabel(t, "modalityValue", obligation.modality);
  }

  if (field === "validFrom") {
    return formatDate(obligation.validFrom);
  }

  if (field === "regulatedObjectCode") {
    return regulatedObjectName(t, obligation.regulatedObjectCode);
  }

  if (field === "roleCode" && obligation.roleCode) {
    return t(`operatorResponsibilityPanel.roleCodeValue.${obligation.roleCode}`, {
      defaultValue: obligation.roleCode,
    });
  }

  if (field === "protectionGoalCode" && obligation.protectionGoalCode) {
    return t(`operatorResponsibilityPanel.protectionGoalValue.${obligation.protectionGoalCode}`, {
      defaultValue: obligation.protectionGoalCode,
    });
  }

  return obligation[field] ?? "—";
};

const isChanged = (previous: any, proposal: any, field: string) =>
  JSON.stringify(previous?.[field] ?? null) !== JSON.stringify(proposal?.[field] ?? null);

export default function DiffReviewPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [pairs, setPairs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [edited, setEdited] = useState<any>(null);
  const [noteDialogAction, setNoteDialogAction] = useState<LegalReviewAction | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const result = await legalAPI.getDiffReviews();

      setPairs(result || []);
      setSelected(null);
      setEdited(null);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const changes = useMemo(() => {
    if (!selected?.proposal || !edited) {
      return {};
    }

    const result: Record<string, any> = {};

    for (const field of ["type", "modality", "regulatedObjectCode", "roleCode", "protectionGoalCode", "description"]) {
      if ((edited[field] ?? null) !== (selected.proposal[field] ?? null)) {
        result[field] = edited[field] ?? null;
      }
    }

    if (JSON.stringify(edited.inspection || {}) !== JSON.stringify(selected.proposal.inspection || {})) {
      result.inspection = edited.inspection;
    }

    return result;
  }, [selected, edited]);

  const isDirty = Object.keys(changes).length > 0;
  const fourEyes = useFourEyes(selected?.proposal);
  const noteTexts = reviewDialogTexts(t, noteDialogAction || "confirm", fourEyes.stage, isDirty, selected?.obligationKey ?? "");
  const confirmTexts = reviewDialogTexts(t, "confirm", fourEyes.stage, false, selected?.obligationKey ?? "");

  const submitReview = async (action: LegalReviewAction, note: string) => {
    setSubmitting(true);

    try {
      await legalAPI.reviewObligation({
        obligationKey: selected.obligationKey,
        action,
        changes: action === "confirm" && isDirty ? changes : undefined,
        reviewNote: note || undefined,
      });

      setNoteDialogAction(null);
      setReviewNote("");
      await load();
      properties.onReviewed();
      pushSuccess(t(`operatorResponsibilityPanel.reviewed.${action}`));
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const startAction = (action: LegalReviewAction) => {
    if (action === "confirm" && !isDirty) {
      // Unveränderte Freigabe braucht keine Anmerkung — aber eine Rückfrage,
      // weil sie protokolliert und nur angehängt wird.
      setConfirmDialogOpen(true);
    } else {
      setReviewNote("");
      setNoteDialogAction(action);
    }
  };

  if (loading) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <Skeleton className="h-6 w-[320px] max-w-full" />
        <Skeleton className="h-[180px] w-full" />
      </div>
    );
  }

  if (pairs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <p className="text-sm font-semibold">
          {t("operatorResponsibilityPanel.noDiffReviewsTitle")}
        </p>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t("operatorResponsibilityPanel.noDiffReviews")}
        </p>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("operatorResponsibilityPanel.diffReviewIntro", { count: pairs.length })}
        </p>
        <div className="flex min-w-0 flex-col">
          {pairs.map((pair: any) => (
            <button
              key={pair.obligationKey}
              type="button"
              className="flex min-w-0 flex-wrap items-center gap-3 border-b py-3 text-left hover:bg-accent"
              onClick={() => {
                setSelected(pair);
                setEdited({ ...pair.proposal, inspection: { ...(pair.proposal?.inspection || {}) } });
              }}
            >
              {/* Ein Schlüssel wie `dguv-v3-vorlage/voiceAlarmSystem/wiederkehrende-pruefung` hat keine
                  Trennstelle und lief in die Feldspalte daneben. */}
              <span className="w-[260px] min-w-0 max-w-full font-semibold [overflow-wrap:anywhere]">
                {pair.obligationKey}
              </span>
              <span className="min-w-0 flex-1 break-words text-sm">
                {DIFF_FIELDS.filter((field) => isChanged(pair.previous, pair.proposal, field))
                  .map((field) => obligationFieldLabel(t, field))
                  .join(", ")}
              </span>
              <EffectivePendingWarning obligation={pair.proposal} compact />
              <ReviewStatusBadge status={pair.proposal?.reviewStatus} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const changedFields = DIFF_FIELDS.filter((field) => isChanged(selected.previous, selected.proposal, field));
  const descriptionId = `${fieldId}-description`;
  const typeId = `${fieldId}-type`;
  const modalityId = `${fieldId}-modality`;
  const regulatedObjectId = `${fieldId}-regulated-object`;
  const noteId = `${fieldId}-note`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h3 className="min-w-0 break-words text-lg font-semibold">{selected.obligationKey}</h3>
          <ProvenanceBadge
            provenanceType={selected.proposal?.provenanceType}
            generatedBy={selected.proposal?.generatedBy}
          />
        </div>
        <Button variant="outline" className="gap-2 max-sm:w-full" onClick={() => setSelected(null)}>
          <ArrowLeft aria-hidden className="h-4 w-4" />
          {t("global.back")}
        </Button>
      </div>

      <EffectivePendingWarning obligation={selected.proposal} />

      <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(320px,1fr))] items-start gap-6">
        <Section title={t("operatorResponsibilityPanel.sourceProvision")}>
          <p className="min-w-0 break-words font-semibold">{selected.provision?.citation}</p>
          <div className="mt-3 max-h-[480px] min-w-0 overflow-y-auto whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
            {selected.provision?.text || t("operatorResponsibilityPanel.provisionTextUnavailable")}
          </div>
        </Section>

        <Section title={t("operatorResponsibilityPanel.oldVersusNew")}>
          <div className="min-w-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">
                    {t("operatorResponsibilityPanel.field")}
                  </TableHead>
                  <TableHead>
                    <span className="flex flex-wrap items-center gap-2">
                      {t("operatorResponsibilityPanel.previousConfirmed", {
                        version: selected.previous?.version,
                      })}
                      <ReviewStatusBadge status={selected.previous?.reviewStatus} />
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="flex flex-wrap items-center gap-2">
                      {t("operatorResponsibilityPanel.newProposal", {
                        version: selected.proposal?.version,
                      })}
                      <ReviewStatusBadge status={selected.proposal?.reviewStatus} />
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DIFF_FIELDS.map((field) => {
                  const changed = changedFields.includes(field);

                  return (
                    <TableRow key={field}>
                      <TableCell className="min-w-0 break-words font-semibold">
                        <span className="flex flex-wrap items-center gap-2">
                          {obligationFieldLabel(t, field)}
                          {/* Die Änderung wird benannt, nicht nur eingefärbt. */}
                          {changed ? (
                            <Badge variant="warning">
                              {t("operatorResponsibilityPanel.fieldChanged")}
                            </Badge>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell
                        className={`min-w-0 break-words ${changed ? "text-destructive" : ""}`}
                      >
                        {String(fieldValue(t, selected.previous, field))}
                      </TableCell>
                      <TableCell
                        className={`min-w-0 break-words ${changed ? "bg-warning/10 font-semibold" : ""}`}
                      >
                        {String(fieldValue(t, selected.proposal, field))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="mt-6 flex min-w-0 flex-col gap-4">
            <h4 className="text-sm font-semibold">
              {t("operatorResponsibilityPanel.inlineAdjustment")}
            </h4>

            <div className="flex min-w-0 flex-wrap gap-4">
              <div className="flex min-w-[180px] flex-1 flex-col gap-1.5">
                <Label htmlFor={typeId}>{t("operatorResponsibilityPanel.type")}</Label>
                <Select
                  value={edited?.type ?? ""}
                  onValueChange={(value: string) => setEdited({ ...edited, type: value })}
                >
                  <SelectTrigger id={typeId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {(properties.vocabulary?.obligationTypes || []).map((key: string) => (
                      <SelectItem key={key} value={key}>
                        {t(`operatorResponsibilityPanel.obligationType.${key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-[150px] flex-1 flex-col gap-1.5">
                <Label htmlFor={modalityId}>{t("operatorResponsibilityPanel.modality")}</Label>
                <Select
                  value={edited?.modality ?? ""}
                  onValueChange={(value: string) => setEdited({ ...edited, modality: value })}
                >
                  <SelectTrigger id={modalityId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {(properties.vocabulary?.modalities || []).map((key: string) => (
                      <SelectItem key={key} value={key}>
                        {t(`operatorResponsibilityPanel.modalityValue.${key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
                <Label htmlFor={regulatedObjectId}>
                  {t("operatorResponsibilityPanel.regulatedObject")}
                </Label>
                <Select
                  value={edited?.regulatedObjectCode ?? ""}
                  onValueChange={(value: string) =>
                    setEdited({ ...edited, regulatedObjectCode: value })
                  }
                >
                  <SelectTrigger id={regulatedObjectId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {(properties.regulatedObjects || []).map((object: any) => (
                      <SelectItem key={object.code} value={object.code}>
                        {`${regulatedObjectName(t, object.code, object.name)} (${object.code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={descriptionId}>{t("operatorResponsibilityPanel.description")}</Label>
              <Textarea
                id={descriptionId}
                rows={4}
                value={edited?.description || ""}
                onChange={(event) => setEdited({ ...edited, description: event.target.value })}
              />
            </div>

            <ApprovalTrail obligation={selected?.proposal} />

            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Button
                className="gap-2 max-sm:w-full"
                disabled={submitting || !fourEyes.canApprove}
                onClick={() => startAction("confirm")}
              >
                {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                {approveLabel(t, fourEyes.stage, isDirty)}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={submitting || !fourEyes.canVeto}
                onClick={() => startAction("needsRevision")}
              >
                {t("operatorResponsibilityPanel.needsRevision")}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={submitting || !fourEyes.canVeto}
                onClick={() => startAction("reject")}
              >
                {t("operatorResponsibilityPanel.reject")}
              </Button>
              <ApprovalBlockHint obligation={selected?.proposal} />
            </div>
          </div>
        </Section>
      </div>

      {/* Entscheidung mit Anmerkung: Der Text ist Pflicht, weil eine Korrektur
          oder Ablehnung ohne Begründung später nicht mehr erklärbar ist. */}
      <Dialog
        open={!!noteDialogAction}
        onOpenChange={(open) => {
          if (!open) setNoteDialogAction(null);
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {noteTexts.title}
            </DialogTitle>
            <DialogDescription className="min-w-0 break-words">
              {noteTexts.consequence}
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={noteId}>{t("operatorResponsibilityPanel.reviewNote")}</Label>
            <Textarea
              id={noteId}
              rows={4}
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
            />
            <p className="min-w-0 break-words text-xs text-muted-foreground">
              {t("operatorResponsibilityPanel.reviewNoteRequired")}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setNoteDialogAction(null)}
            >
              {t("global.cancel")}
            </Button>
            <Button
              className="gap-2 max-sm:w-full"
              disabled={submitting || reviewNote.trim().length === 0}
              onClick={() => submitReview(noteDialogAction as LegalReviewAction, reviewNote.trim())}
            >
              {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
              {noteTexts.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unveränderte Freigabe: kein Text nötig, aber die Folge steht dabei. */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTexts.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {confirmTexts.consequence}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                setConfirmDialogOpen(false);
                submitReview("confirm", "");
              }}
            >
              {confirmTexts.submit}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
